import { createResource, type RouteResourceResponse } from 'react-resource-router';

import { eocUserResource } from '@atlassian/eoc-resources';

import {
	getContact,
	getForwardingRules,
	getMuteDetail,
	getNotificationPreferences,
	getQuietHoursDetail,
} from '../endpoints';

// Redefined here since it is not exported from RRR.
type ResourceDependencies = {
	[type: string]: RouteResourceResponse;
};

const createNotificationPreferencesResource = (
	checkConsolidationOnRouteDependency?: (dependencies: ResourceDependencies) => boolean,
	depends?: string[],
) =>
	createResource({
		type: 'notificationPreferencesResource',
		getKey: () => 'notificationPreferencesResource',
		getData: ({ dependencies }) => {
			const isConsolidated = checkConsolidationOnRouteDependency
				? // @ts-ignore - Type 'undefined' is not assignable to type 'RouteResourceResponse'.
					// This was added when enrolling @atlassian/eoc-notifications into JFE local consumption.
					checkConsolidationOnRouteDependency(dependencies)
				: true;

			if (!isConsolidated) {
				return null;
			}
			return getNotificationPreferences();
		},
		isBrowserOnly: true,
		depends,
	});

const createContactsResource = (
	checkConsolidationOnRouteDependency?: (dependencies: ResourceDependencies) => boolean,
	depends?: string[],
) =>
	createResource({
		type: 'contactsResource',
		getKey: () => 'contactsResource',
		getData: async ({ dependencies }) => {
			const isConsolidated = checkConsolidationOnRouteDependency
				? // @ts-ignore - Type 'undefined' is not assignable to type 'RouteResourceResponse'.
					// This was added when enrolling @atlassian/eoc-notifications into JFE local consumption.
					checkConsolidationOnRouteDependency(dependencies)
				: true;

			if (!isConsolidated) {
				return null;
			}
			const data = await getContact();
			return data.contacts;
		},
		isBrowserOnly: true,
		depends,
	});

const createMuteResource = (
	checkConsolidationOnRouteDependency?: (dependencies: ResourceDependencies) => boolean,
	depends?: string[],
) =>
	createResource({
		type: 'muteResource',
		getKey: () => 'muteResource',
		getData: ({ dependencies }) => {
			const isConsolidated = checkConsolidationOnRouteDependency
				? // @ts-ignore - Type 'undefined' is not assignable to type 'RouteResourceResponse'.
					// This was added when enrolling @atlassian/eoc-notifications into JFE local consumption.
					checkConsolidationOnRouteDependency(dependencies)
				: true;

			if (!isConsolidated) {
				return null;
			}
			return getMuteDetail();
		},
		isBrowserOnly: true,
		depends,
	});

const createQuietHoursResource = (
	checkConsolidationOnRouteDependency?: (dependencies: ResourceDependencies) => boolean,
	depends?: string[],
) =>
	createResource({
		type: 'quietHoursResource',
		getKey: () => 'quietHoursResource',
		getData: ({ dependencies }) => {
			const isConsolidated = checkConsolidationOnRouteDependency
				? // @ts-ignore - Type 'undefined' is not assignable to type 'RouteResourceResponse'.
					// This was added when enrolling @atlassian/eoc-notifications into JFE local consumption.
					checkConsolidationOnRouteDependency(dependencies)
				: true;

			if (!isConsolidated) {
				return null;
			}
			return getQuietHoursDetail();
		},
		isBrowserOnly: true,
		depends,
	});

const createForwardingRulesResource = (
	checkConsolidationOnRouteDependency?: (dependencies: ResourceDependencies) => boolean,
	depends?: string[],
) =>
	createResource({
		type: 'forwardingRulesResource',
		getKey: () => 'forwardingRulesResource',
		getData: ({ dependencies }) => {
			const isConsolidated = checkConsolidationOnRouteDependency
				? // @ts-ignore - Type 'undefined' is not assignable to type 'RouteResourceResponse'.
					// This was added when enrolling @atlassian/eoc-notifications into JFE local consumption.
					checkConsolidationOnRouteDependency(dependencies)
				: true;

			if (!isConsolidated) {
				return null;
			}

			return getForwardingRules().then(({ values }) => values);
		},
		isBrowserOnly: true,
		depends,
	});

export {
	createContactsResource,
	createNotificationPreferencesResource,
	eocUserResource,
	createMuteResource,
	createQuietHoursResource,
	createForwardingRulesResource,
};
